// src/components/BookingForm/BookingForm.js
import React, { useState, useEffect } from 'react';
import { textdb, serverTimestamp } from '../../firebaseConfig'; // Adjust path if needed
import { collection, addDoc } from "firebase/firestore";
import './BookingForm.css'; // Make sure CSS file exists

const BookingForm = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        whatsappNumber: '',
        weight: '',
        age: '',
        investmentComfort: '', // State for the new question
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    const paymentUrl = "https://u.payu.in/fItYFflzhuHn"; // Your specific payment link

    // Effect to check if already submitted in this session
    useEffect(() => {
        const submittedFlag = localStorage.getItem('bookingFormSubmitted');
        if (submittedFlag === 'true') {
            setIsSubmitted(true);
            setError('You have already submitted your details. Redirecting to payment...');
            // Redirect after a delay
            const redirectTimeout = setTimeout(() => {
                window.location.href = paymentUrl;
            }, 2500); // 2.5 seconds delay

            // Cleanup timeout if component unmounts before redirect
            return () => clearTimeout(redirectTimeout);
        }
    }, [paymentUrl]); // Dependency array includes paymentUrl

    // Handles changes in form inputs (text, number, radio)
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    // Handles form submission
    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevent default form submission
        setError(''); // Clear previous errors

        // Validation Checks
        if (!formData.fullName || !formData.whatsappNumber || !formData.weight || !formData.age || !formData.investmentComfort) {
            setError('Please fill in all fields, including the investment question.');
            return;
        }
        if (!/^\d+$/.test(formData.whatsappNumber) || formData.whatsappNumber.length < 10) {
            setError('Please enter a valid WhatsApp number (digits only, at least 10 digits).');
            return;
        }
        if (isNaN(formData.weight) || formData.weight <= 0 || isNaN(formData.age) || formData.age <= 0) {
            setError('Please enter valid positive numbers for weight and age.');
            return;
        }

        setIsSubmitting(true); // Disable button

        try {
            // Add data to Firestore "bookings" collection
            const docRef = await addDoc(collection(textdb, "bookings"), {
                ...formData, // Includes all form data fields
                submittedAt: serverTimestamp() // Firestore server timestamp
            });
            console.log("Booking submitted with ID: ", docRef.id);

            // Mark as submitted in local storage
            localStorage.setItem('bookingFormSubmitted', 'true');
            setIsSubmitted(true); // Update state to show submitted message

            // Redirect to payment page after a short delay
            setTimeout(() => {
                 window.location.href = paymentUrl;
            }, 500); // 0.5 second delay

        } catch (err) {
            console.error("Error adding document: ", err);
            setError('Failed to submit details. Please check your connection and try again.');
            setIsSubmitting(false); // Re-enable button on error
        }
    };

    // Render submitted state message and redirect info
    if (isSubmitted) {
        return (
            <div className="booking-form-container submitted-message" id="booking-form-section">
                <h4>Thank You!</h4>
                <p>{error || 'Your details have been submitted. Redirecting to payment page...'}</p>
                 {/* Fallback link in case automatic redirect fails */}
                 <a href={paymentUrl} className="manual-redirect-link">Proceed to Payment Manually</a>
            </div>
        );
    }

    // Render the form
    return (
        // Container div with ID for scrolling
        <div className="booking-form-container" id="booking-form-section">
            <form onSubmit={handleSubmit} className="booking-form">
                <h4>Book Your Transformation Call</h4>
                {error && <p className="error-message">{error}</p>}

                {/* Full Name */}
                <div className="form-group">
                    <label htmlFor="fullName">Full Name</label>
                    <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        placeholder="Enter your full name"
                    />
                </div>

                {/* WhatsApp Number */}
                <div className="form-group">
                    <label htmlFor="whatsappNumber">WhatsApp Number (e.g., 91XXXXXXXXXX)</label>
                    <input
                        type="tel" // Use 'tel' type for phone numbers
                        id="whatsappNumber"
                        name="whatsappNumber"
                        value={formData.whatsappNumber}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        pattern="[0-9]*" // Basic pattern for digits
                        minLength="10"
                        placeholder="Enter your WhatsApp number"
                    />
                </div>

                {/* Weight and Age Row */}
                <div className="form-row">
                    <div className="form-group form-group-half">
                        <label htmlFor="weight">Current Weight (kg)</label>
                        <input
                            type="number"
                            id="weight"
                            name="weight"
                            value={formData.weight}
                            onChange={handleChange}
                            required
                            disabled={isSubmitting}
                            min="1"
                            placeholder="e.g., 85"
                        />
                    </div>
                    <div className="form-group form-group-half">
                        <label htmlFor="age">Age</label>
                        <input
                            type="number"
                            id="age"
                            name="age"
                            value={formData.age}
                            onChange={handleChange}
                            required
                            disabled={isSubmitting}
                            min="1"
                            placeholder="e.g., 30"
                        />
                    </div>
                </div>

                {/* Investment Commitment Question */}
                <div className="form-group investment-question">
                    <label className="investment-label">Investment Commitment</label>
                    <p className="investment-context">
                    If I decide to take you up as a trainee after the call, the investment will start at 25,000 INR for a minimum commitment of 12 weeks (WITH PAYMENT OPTIONS AVAILABLE)


                    </p>
                    <div className="radio-options">
                        {/* Option 1: Yes */}
                        <div className="radio-option">
                            <input
                                type="radio"
                                id="investYes"
                                name="investmentComfort"
                                value=" Thats perfectly affordable for me"
                                checked={formData.investmentComfort === " Thats perfectly affordable for me"}
                                onChange={handleChange}
                                required // Make selecting an option mandatory
                                disabled={isSubmitting}
                            />
                            <label htmlFor="investYes"> Thats perfectly affordable for me</label>
                        </div>
                        {/* Option 2: Decide Later */}
                        <div className="radio-option">
                            <input
                                type="radio"
                                id="investDecideLater"
                                name="investmentComfort"
                                value="Thats affordable but I need to know how the program works before deciding"
                                checked={formData.investmentComfort === "Thats affordable but I need to know how the program works before deciding"}
                                onChange={handleChange}
                                disabled={isSubmitting}
                            />
                            <label htmlFor="investDecideLater">Thats affordable but I need to know how the program works before deciding</label>
                        </div>
                        {/* Option 3: No */}
                        <div className="radio-option">
                            <input
                                type="radio"
                                id="investNo"
                                name="investmentComfort"
                                value="This is beyond my budget"
                                checked={formData.investmentComfort === "This is beyond my budget"}
                                onChange={handleChange}
                                disabled={isSubmitting}
                            />
                            <label htmlFor="investNo">This is beyond my budget</label>
                        </div>
                    </div>
                </div>

                {/* Submit Button */}
                <button type="submit" className="submit-button" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Book the Call'}
                </button>
            </form>
        </div>
    );
};

export default BookingForm;